import React from "react";
import ReactDOM from "react-dom/client";
import { onlineManager } from "@tanstack/react-query";
import { theme } from "./theme";
import { AppConfigurationProvider, defaultLocalizationStorageImpl, Fonts, getLocaleParts, getUserLocale, localizationService } from "am-tax-fe-core";
import { ChakraProvider } from "@chakra-ui/react";
import { Middleware } from "openapi-fetch";
import { configureApiClient } from "./api";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { UnauthenticatedLandingPage } from "./pages/UnauthenticatedLandingPage";
import { PageRoutes } from "./pages";
import { getAppState } from "./state/useAppState";
import { useMsal } from "@azure/msal-react";
import { getApiErrorResponse, isRevokedApiKeyError, redirectToRevokedApiKeySplash } from "./utils/apiKeyErrors";

//import the fe-core style sheet (fe-core bundles up all the stylesheets that are included from third party libs and exports them as a single css file)
import "am-tax-fe-core/index.css";
import { initializeI18n } from "./initializeI18n";
import { IPublicClientApplication } from "@azure/msal-browser";
import { isInIframe } from "./config/authConfig";
import { settings } from "./utils/settings";

// Store MSAL instance globally for error handler access
let msalInstanceGlobal: IPublicClientApplication | null = null;

window.addEventListener("unhandledrejection", event => {
    if (isInIframe() && event.reason?.errorCode === "redirect_in_iframe" && event.reason?.name === "BrowserAuthError") {
        console.warn("MSAL iframe redirect error detected - attempting popup logout", event);
        event.preventDefault();

        // It does a logout with popup instead of redirect
        if (msalInstanceGlobal) {
            msalInstanceGlobal
                .logoutPopup({
                    mainWindowRedirectUri: window.location.origin,
                })
                .catch((error: Error) => {
                    console.error("Popup logout failed:", error);
                });
        } else {
            console.warn("MSAL instance not available for popup logout");
        }
    }
});

function configureHttpClient(baseUrl: string, getAccessToken: () => Promise<string>) {
    const msalMiddleware: Middleware = {
        async onRequest({ request }) {
            const apiKey = getAppState().apiKey;
            if (!settings.VITE_BYPASS_AUTH) {
                const accessToken = await getAccessToken();
                request.headers.append("Authorization", `Bearer ${accessToken}`);
            }
            // Apply the api key even when MSAL is bypassed, so bypass mode can still hit the real backend.
            if (apiKey) {
                request.headers.set("apiKey", apiKey);
            }
            return request;
        },
        async onResponse({ response }) {
            if (response.status === 401) {
                const errorData = await getApiErrorResponse(response);
                if (isRevokedApiKeyError(errorData, response.status)) {
                    redirectToRevokedApiKeySplash();
                }
            }

            return response;
        },
    };

    configureApiClient(baseUrl, [msalMiddleware]);
}

// Component wrapper to capture MSAL instance
function AppWithMsal() {
    const { instance } = useMsal();

    // Store MSAL instance globally when component mounts
    React.useEffect(() => {
        msalInstanceGlobal = instance;
    }, [instance]);

    return (
        <>
            <PageRoutes />
            {settings.VITE_ENABLE_REACT_QUERY_DEV_TOOLS && <ReactQueryDevtools initialIsOpen={false} />}
        </>
    );
}

(async function () {
    // am-tax-fe-core's AppConfigurationProvider caches /env.json in sessionStorage under "envConfig" and
    // never revalidates it, so clear it here to force a fresh fetch on every page load.
    sessionStorage.removeItem("envConfig");

    if (settings.VITE_BYPASS_AUTH || settings.VITE_MOCK_PARTIAL_BACKEND || settings.VITE_MOCK_ENTIRE_BACKEND) {
        // this probably seems backwards, but we want to tell react query to act like it is online even if it is offline
        onlineManager.setOnline(true);

        const setup = await import("./api/msw/setupForBrowser");
        const { getWorker } = setup;
        const worker = await getWorker();
        await worker.start({ onUnhandledRequest: "bypass" });
    }

    // If you are translating the app to any additional languages, add the locales here.
    const supportedLanguageLocales = ["en-US"];

    // initialize the localization service with the supported languages
    await localizationService.init(defaultLocalizationStorageImpl, supportedLanguageLocales);

    // initialize the translation service with the user's language pref
    const i18n = await initializeI18n(getLocaleParts(getUserLocale()).lang);

    // set up the event handler to update i18Next when the user changes their language settings
    localizationService.onSettingsChanged(settings => {
        i18n.changeLanguage(getLocaleParts(settings?.userLocale).lang || "en");
    });

    const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

    root.render(
        <React.StrictMode>
            <ChakraProvider theme={theme} resetCSS>
                <Fonts />
                <AppConfigurationProvider
                    envJsonUrl={"/env.json"}
                    byPassAuth={settings.VITE_BYPASS_AUTH || settings.VITE_MOCK_ENTIRE_BACKEND}
                    unauthenticatedLandingPage={<UnauthenticatedLandingPage />}
                    onApiContextInit={configureHttpClient}
                    enableAutoLogout={!settings.VITE_DISABLE_AUTO_LOGOUT}
                    enableAppInsights={settings.VITE_ENABLE_APP_INSIGHTS}
                    version={{
                        ver: settings.VITE_BUILD_COMMIT,
                        build: settings.VITE_BUILD_NUMBER,
                    }}
                >
                    <AppWithMsal />
                </AppConfigurationProvider>
            </ChakraProvider>
        </React.StrictMode>,
    );
})();
